import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import { PrLegend, PrMatrixTable } from '../../components/progress/PrStatus';
import { NOTE_STATUS_AR, PIPELINE, STAGE_AR, STAGE_BADGE } from '../../components/books/BookStatus';
import { fmtDateShort } from '../../utils/dates';

const NOTE_COLORS = {
  new: 'var(--c-warning)',
  revised: 'var(--c-blue)',
  accepted: 'var(--c-success)',
  pending: 'var(--c-coral)',
};

export default function BooksMonthlyReport({ month: monthProp, printable = false }) {
  const params = useParams();
  const { api } = useAuth();
  const month = monthProp || params.month || '';

  const [selected, setSelected] = useState(month);
  const [months, setMonths] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    const qs = selected ? `?month=${selected}` : '';
    setLoading(true);
    api(`/book-summaries/report/monthly${qs}`)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api, selected]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api('/book-summaries/report/months').then(setMonths).catch(() => {});
  }, []);

  useEffect(() => {
    if (printable && data) {
      const t = setTimeout(() => window.print(), 600);
      return () => clearTimeout(t);
    }
  }, [printable, data]);

  if (loading) return <div className="loader" />;
  if (!data) {
    return (
      <div className="card">
        <div className="empty"><div className="empty-icon">📅</div>لا يمكن تحميل التقرير</div>
      </div>
    );
  }

  const notePie = Object.entries(data.note_totals)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: NOTE_STATUS_AR[k], value: v, key: k }));

  const stageBar = PIPELINE.concat('approved')
    .map(k => ({ name: STAGE_AR[k], عدد: data.stage_totals[k] || 0 }))
    .filter(r => r['عدد'] > 0);

  return (
    <div className={printable ? 'public-report report-mode' : 'report-mode'}>
      <header
        className="report-cover"
        style={!printable ? { borderRadius: 10, overflow: 'hidden', marginBottom: 16 } : undefined}
      >
        <div className="report-cover-inner">
          <div className="report-cover-text">
            <div className="report-cover-kicker">تقرير تلخيص الكتب</div>
            <h1 className="report-cover-title">مشروع تلخيص الكتب</h1>
            <div className="report-cover-period">{data.label}</div>
          </div>
        </div>
      </header>

      <main className={printable ? 'public-main' : undefined}>
        {printable ? (
          <button
            className="btn btn-primary btn-sm no-print"
            style={{ marginBottom: 12 }}
            onClick={() => window.print()}
          >
            🖨 طباعة / حفظ PDF
          </button>
        ) : (
          <div className="filters" style={{ marginBottom: 16 }}>
            <Link className="btn btn-outline btn-sm" to="/book-summaries">← تلخيص الكتب</Link>
            <select className="form-select" value={selected} onChange={e => setSelected(e.target.value)}>
              {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <Link
              className="btn btn-outline btn-sm"
              to={`/book-summaries/report/print/${data.month}`}
              target="_blank"
              rel="noreferrer"
            >
              🖨 نسخة للطباعة
            </Link>
          </div>
        )}

        <div className="public-stats-row" style={{ marginBottom: 18 }}>
          <Stat value={data.totals.books} label="كتب في التقرير" />
          <Stat value={data.totals.assigned} label="قيد التلخيص" />
          <Stat value={data.totals.in_review} label="قيد المراجعة والتعديل" />
          <Stat value={data.totals.awaiting_approval} label="بانتظار الاعتماد" />
          <Stat value={data.totals.approved} label="اعتُمد هذا الشهر" />
          <Stat value={data.totals.overdue} label="متأخّر" danger={data.totals.overdue > 0} />
        </div>

        <section className="report-section">
          <div className="report-section-header"><h3>مسار الكتب</h3></div>
          <PrMatrixTable matrix={data.matrix} />
          <div className="report-table-wrap"><PrLegend /></div>
        </section>

        {data.books.length > 0 && (
          <section className="report-section">
            <div className="report-section-header"><h3>تفصيل الكتب</h3></div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>الكتاب</th>
                    <th>المرحلة</th>
                    <th>الباحث</th>
                    <th>المراجع</th>
                    <th>الجولات</th>
                    <th>المهلة</th>
                    <th>معلّق</th>
                    <th>أيام حتى الاعتماد</th>
                  </tr>
                </thead>
                <tbody>
                  {data.books.map(b => (
                    <tr key={b.id}>
                      <td>
                        {b.book_title}
                        {b.book_author && (
                          <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>{b.book_author}</div>
                        )}
                      </td>
                      <td><span className={`badge badge-${STAGE_BADGE[b.status]}`}>{b.status_label}</span></td>
                      <td style={{ fontSize: 13 }}>{b.researcher_name || '—'}</td>
                      <td style={{ fontSize: 13 }}>{b.reviewer_name || '—'}</td>
                      <td>{b.rounds_count}</td>
                      <td style={{ fontSize: 12, color: b.is_overdue ? 'var(--danger)' : undefined }}>
                        {b.stage_due_date ? fmtDateShort(b.stage_due_date) : '—'}
                      </td>
                      <td>{b.notes.pending || '—'}</td>
                      <td>{b.days_to_approval ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {(notePie.length > 0 || stageBar.length > 0) && (
          <section className="report-section">
            <div className="report-section-header"><h3>مؤشّرات</h3></div>
            <div style={{ padding: 'var(--s-4)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
              {notePie.length > 0 && (
                <div style={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={notePie} dataKey="value" nameKey="name" outerRadius={80} label>
                        {notePie.map(entry => (
                          <Cell key={entry.key} fill={NOTE_COLORS[entry.key]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
              {stageBar.length > 0 && (
                <div style={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stageBar}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="عدد" fill="var(--c-blue)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </section>
        )}

        {data.people.length > 0 && (
          <section className="report-section">
            <div className="report-section-header"><h3>أداء الفريق</h3></div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>الاسم</th>
                    <th>كتب كباحث</th>
                    <th>اعتُمد له</th>
                    <th>متأخّر عليه</th>
                    <th>جولات كمراجع</th>
                    <th>بنود أثارها</th>
                    <th>متوسط أيام الاعتماد</th>
                  </tr>
                </thead>
                <tbody>
                  {data.people.map(p => (
                    <tr key={p.person_id}>
                      <td>{p.name || '—'}</td>
                      <td>{p.as_researcher}</td>
                      <td>{p.approved}</td>
                      <td style={{ color: p.overdue > 0 ? 'var(--danger)' : undefined }}>{p.overdue}</td>
                      <td>{p.as_reviewer}</td>
                      <td>{p.notes_raised}</td>
                      <td>{p.avg_days_to_approval ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* ملحق البنود المعلّقة — هذا ما يُرفق بالملف العام */}
        <section className="report-section">
          <div className="report-section-header"><h3>ملحق: البنود المعلّقة</h3></div>
          {data.pending_items.length === 0 ? (
            <div className="report-empty">لا بنود معلّقة في هذا الشهر</div>
          ) : (
            data.pending_items.map(group => (
              <div key={group.summary_id} style={{ padding: 'var(--s-4) var(--s-5) 0' }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>{group.book_title}</div>
                <ol style={{ margin: 0, paddingInlineStart: 22 }}>
                  {group.items.map(item => (
                    <li key={item.seq} style={{ marginBottom: 6, fontSize: 13 }}>
                      <span>{item.body}</span>
                      {(item.location || item.page_no) && (
                        <span style={{ color: 'var(--text-faint)' }}>
                          {' '}({[item.location, item.page_no ? `ص ${item.page_no}` : null]
                            .filter(Boolean).join(' — ')})
                        </span>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            ))
          )}
        </section>

        <div className="report-footer" style={{ marginTop: 20 }}>
          أُنشئ في {data.generated_at}
        </div>
      </main>
    </div>
  );
}

function Stat({ value, label, danger }) {
  return (
    <div className="public-stat-card">
      <div className="public-stat-value" style={danger ? { color: 'var(--danger)' } : undefined}>{value}</div>
      <div className="public-stat-label">{label}</div>
    </div>
  );
}
